import { useAppDispatch } from "@/app/hooks";
import {
  createCampaignType,
  getCampaignTypeDetails,
  updateCampaignType,
} from "@/features";
import { ChannelType } from "@/types";
import { toastify } from "@/utils";
import { useFormik } from "formik";
import { useCallback, useEffect } from "react";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";

type Props = {
  isOpen: boolean;
  selectedId: number;
  onSuccess: () => void;
  toggle: () => void;
};

export const CreateOrEditCampaignType = ({
  isOpen,
  selectedId,
  onSuccess,
  toggle,
}: Props) => {
  const dispatch = useAppDispatch();

  const formik = useFormik({
    initialValues: {
      name: "",
      position: "",
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required("Please fill out this field."),
      position: Yup.number().optional(),
    }),
    enableReinitialize: true,
    onSubmit: async (values) => {
      const formatValues: ChannelType = {
        id: selectedId,
        name: values.name,
        position: values.position ? Number(values.position) : "",
      };

      try {
        if (selectedId) {
          await dispatch(updateCampaignType(formatValues)).unwrap();
          toastify("Campaign type has been updated successfully", "success");
        } else {
          await dispatch(createCampaignType(formatValues)).unwrap();
          toastify("Campaign type has been created successfully", "success");
        }

        onSuccess();
      } catch (error) {
        console.log("error: ", error);
        toastify("An error occurred. Please try again.", "error");
      } finally {
        toggle();
      }
    },
  });

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = formik;

  const getCampaignTypeDetailsInfo = useCallback(
    async (id: number) => {
      try {
        const details = await dispatch(getCampaignTypeDetails(id)).unwrap();
        setFieldValue("name", details.name);
        setFieldValue("position", details.position || "");
      } catch (error: any) {
        console.log("error: ", error);
      }
    },
    [dispatch, setFieldValue],
  );

  useEffect(() => {
    if (selectedId) {
      getCampaignTypeDetailsInfo(selectedId);
    }
  }, [getCampaignTypeDetailsInfo, selectedId]);

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <form onSubmit={handleSubmit}>
        <ModalHeader toggle={toggle}>
          {selectedId ? "Edit Campaign Type" : "Create Campaign Type"}
        </ModalHeader>
        <ModalBody>
          <div>
            <FormGroup>
              <Label className="required">Campaign Type</Label>
              <Input
                name="name"
                placeholder="Campaign Type"
                value={values.name}
                onChange={handleChange}
                className={errors.name && touched.name ? "is-invalid" : ""}
              />
            </FormGroup>
            {errors.name && touched.name && (
              <p className="text-danger">{errors.name}</p>
            )}
          </div>
          <FormGroup>
            <Label>Position</Label>
            <Input
              name="position"
              placeholder="Position"
              value={values.position}
              onChange={handleChange}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggle} disabled={isSubmitting}>
            Close
          </Button>
          <Button type="submit" color="primary" disabled={isSubmitting}>
            {selectedId ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
